import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { BrandMark } from "@/components/shell/brand-logo";
import { SESSION_ENDED_REDIRECT_SECONDS, signInPathFor } from "@/lib/session-expiry";

/**
 * The one session-ended state (ALL-5718, IAM-134, specs/session-expiry-redirect).
 *
 * Shown instead of the page once GET /auth/session has confirmed the browser
 * session ended. The copy is fixed: no reason code, status, server message or
 * time the app cannot know is ever rendered here. After the countdown (or at
 * once on "Sign in now") the browser does a full navigation to the sign-in page
 * with the validated return path, so no state of the ended session survives.
 */

export const SESSION_ENDED_COPY = {
  title: "Your session has ended",
  body:
    "For your security, Alloy signed you out after a period of inactivity or when your session reached its time limit. Sign in again to pick up where you left off.",
  countdown: (seconds: number) => `Taking you to sign in in ${seconds} second${seconds === 1 ? "" : "s"}…`,
  signIn: "Sign in now",
  opening: "Opening sign-in…",
  differentAccount: "Use a different account",
} as const;

/**
 * ALL-5774: the same state for a browser that was sent to /error because it opened a page
 * that needs a session it does not have (a phone that never signed in, say). It may never
 * have had a session, so it does not claim one ended.
 */
export const SIGN_IN_REQUIRED_COPY = {
  title: "Sign in to continue",
  body: "This page is only available when you are signed in to Alloy. Sign in and you will come back to where you were.",
} as const;

export interface SessionEndedStateProps {
  returnTo: string;
  autoRedirect: boolean;
  /** "ended" (default): a session this tab held has ended. "sign-in-required": no session was found. */
  reason?: "ended" | "sign-in-required";
  /** Injected in tests; defaults to a full browser navigation. */
  navigate?: (url: string) => void;
}

function defaultNavigate(url: string) {
  window.location.assign(url);
}

export function SessionEndedState({ returnTo, autoRedirect, reason = "ended", navigate = defaultNavigate }: SessionEndedStateProps) {
  const heading = reason === "sign-in-required" ? SIGN_IN_REQUIRED_COPY : SESSION_ENDED_COPY;
  const [remaining, setRemaining] = useState(SESSION_ENDED_REDIRECT_SECONDS);
  const [opening, setOpening] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const target = signInPathFor(returnTo);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!autoRedirect || opening) return undefined;
    if (remaining <= 0) {
      setOpening(true);
      navigate(target);
      return undefined;
    }
    const timer = window.setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [autoRedirect, opening, remaining, navigate, target]);

  function signInNow() {
    if (opening) return;
    setOpening(true);
    navigate(target);
  }

  function differentAccount() {
    if (opening) return;
    setOpening(true);
    navigate("/login");
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-bg p-4" data-testid="session-ended-state">
      <div className="auth-card">
        <div className="auth-card__inner">
          <div className="auth-card__content" style={{ maxWidth: 400 }}>
            <div className="grid size-14 place-items-center rounded-xl bg-primary text-white">
              <BrandMark size={32} />
            </div>

            <div className="flex w-full flex-col gap-6">
              <div className="auth-heading">
                <h1 ref={titleRef} tabIndex={-1} className="auth-title outline-none" style={{ fontSize: 24 }}>
                  {heading.title}
                </h1>
                <p className="auth-subtitle" style={{ fontSize: 15 }}>
                  {heading.body}
                </p>
              </div>

              {autoRedirect && (
                <div className="flex w-full items-center gap-2 rounded-lg bg-primary-soft px-3.5 py-3 text-left">
                  <Clock className="size-3.5 shrink-0 text-primary" aria-hidden />
                  <p className="text-xs leading-relaxed text-primary" aria-live="polite">
                    {opening ? SESSION_ENDED_COPY.opening : SESSION_ENDED_COPY.countdown(Math.max(remaining, 0))}
                  </p>
                </div>
              )}

              <div className="flex w-full flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={signInNow}
                  disabled={opening}
                  className="flex h-11 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-white outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary-soft disabled:opacity-70"
                >
                  {opening ? SESSION_ENDED_COPY.opening : SESSION_ENDED_COPY.signIn}
                </button>
                <button
                  type="button"
                  onClick={differentAccount}
                  disabled={opening}
                  className="text-xs font-medium text-muted outline-none hover:underline focus-visible:underline"
                >
                  {SESSION_ENDED_COPY.differentAccount}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
